import React from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useUserRolePermissions } from 'hooks';
import ExpirationDateDisplay from './ExpirationDateDisplay';
import { compartmentsNumber, trailerCapacity } from '../utils';
import TrailerStatusChip from '../components/TrailerStatusChip';

const cardStyles = {
  boxShadow: 6,
  width: 310,
};

const buttonStyles = {
  '&, :hover': {
    boxShadow: 'none',
  },
  height: 33,
  width: 130,
};

export const TrailerCardSkeleton = () => (
  <Card sx={cardStyles}>
    <Box sx={{ p: 2, pb: 0 }}>
      <Skeleton variant="text" />
    </Box>
    <CardContent
      sx={{
        display: 'grid',
        gridColumnGap: 16,
        gridRowGap: 8,
        gridTemplateColumns: '1fr 1fr',
        justifyItems: 'center',
      }}
    >
      <Divider />
      <Divider />
      <Skeleton height={20} variant="text" width={100} />
      <Skeleton height={20} variant="text" width={100} />
      <Skeleton height={30} variant="rectangular" width={66} />
      <Skeleton height={30} variant="rectangular" width={66} />
      <Skeleton
        height={60}
        sx={{ gridColumn: '1/3', mb: 3, mt: 2 }}
        variant="rectangular"
        width={278}
      />
      <Skeleton
        sx={{ '&, :hover': { boxShadow: 'none' }, borderRadius: 2, height: 33, width: 104 }}
        variant="rectangular"
      />
      <Skeleton
        sx={{ '&, :hover': { boxShadow: 'none' }, borderRadius: 2, height: 33, width: 104 }}
        variant="rectangular"
      />
    </CardContent>
  </Card>
);

const TrailerCard = ({ trailer, onViewTrailer, onEditTrailer }) => {
  const { trailerId, vaporTightnessCode, compartments = [], expirationDate, status } = trailer;
  const { isDispatchingManage } = useUserRolePermissions();

  const handleViewClick = () => {
    onViewTrailer(trailerId);
  };

  const handleEditClick = () => {
    onEditTrailer(trailerId);
  };

  return (
    <Card sx={cardStyles}>
      <CardHeader
        slotProps={{
          title: { variant: 'h6' },
        }}
        sx={{ p: 2, pb: 0 }}
        title={R.toUpper(vaporTightnessCode)}
      />
      <Box sx={{ px: 2, py: 0 }}>
        <TrailerStatusChip status={status} />
      </Box>
      <CardContent
        sx={{ display: 'grid', gridColumnGap: 16, gridRowGap: 8, gridTemplateColumns: '1fr 1fr' }}
      >
        <Divider />
        <Divider />
        <Typography sx={{ color: 'secondary', textAlign: 'center' }} variant="body2">
          Compartments
        </Typography>
        <Typography sx={{ color: 'secondary', textAlign: 'center' }} variant="body2">
          Capacity (Gal)
        </Typography>
        <Typography sx={{ mb: 2, textAlign: 'center' }} variant="h5">
          {compartmentsNumber(compartments)}
        </Typography>
        <Typography sx={{ mb: 2, textAlign: 'center' }} variant="h5">
          {trailerCapacity(compartments)}
        </Typography>
        <ExpirationDateDisplay date={expirationDate} />
        <Stack
          direction="row"
          spacing={2}
          sx={{ gridColumn: '1 / span 2', justifyContent: 'center' }}
        >
          <Button onClick={handleViewClick} sx={buttonStyles} variant="outlined">
            View
          </Button>
          {isDispatchingManage && (
            <Button onClick={handleEditClick} sx={buttonStyles} variant="contained">
              Edit
            </Button>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

TrailerCard.propTypes = {
  onEditTrailer: PropTypes.func.isRequired,
  onViewTrailer: PropTypes.func.isRequired,
  trailer: PropTypes.shape({
    compartments: PropTypes.arrayOf(PropTypes.shape({})),
    documents: PropTypes.shape({}),
    expirationDate: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date),
      PropTypes.number,
    ]),
    status: PropTypes.string,
    trailerId: PropTypes.string,
    vaporTightnessCode: PropTypes.string,
  }).isRequired,
};

export default TrailerCard;
